import { ConsoleLogger } from '@proposify/frontend-observability';
import { useEffect, useRef, useState } from 'react';
import { useViewerInfo } from '../identify-viewer/useViewerInfo';
import { observeVisibility } from './observeVisibility';
import { TrackingService } from './TrackingService';

const VIEW_ZONE_SELECTOR = '.preview__main';
const SECTION_CONTAINER_SELECTOR = '.preview-section-container';
const SECTION_SELECTOR = '.preview__editor__wrapper__grid';
const SECTION_TITLE_SELECTOR = '[data-testid="section-title-typography"]';

const MAX_ATTEMPTS = 5;

const VIEWING_SESSION_KEY = 'viewingSessionId';

function getOrCreateViewingSessionId(): string {
  try {
    const stored = sessionStorage.getItem(VIEWING_SESSION_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (typeof parsed === 'string' && parsed.trim().length > 0) {
        return parsed;
      }
    }
    const newId = crypto.randomUUID();
    sessionStorage.setItem(VIEWING_SESSION_KEY, JSON.stringify(newId));
    return newId;
  } catch {
    return crypto.randomUUID();
  }
}

export const useTrackViewing = ({ documentId, accountId }: { documentId: string; accountId?: string }) => {
  const { viewer } = useViewerInfo();
  const sessionIdRef = useRef<string>(getOrCreateViewingSessionId());

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!viewer.id) return;

    let attempts = 0;
    let delay = 1000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const verifyIfPageIsReady = () => {
      const viewZone = document.querySelector(VIEW_ZONE_SELECTOR);
      if (viewZone) {
        setIsReady(true);
        ConsoleLogger.log(`useTrackViewing: ready after ${attempts} attempts to find '${VIEW_ZONE_SELECTOR}'`);
      } else {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          ConsoleLogger.error(`useTrackViewing: element '${VIEW_ZONE_SELECTOR}' not found after ${MAX_ATTEMPTS} attempts`);
        } else {
          delay *= 1.5;
          timeoutId = setTimeout(verifyIfPageIsReady, delay);
        }
      }
    };

    verifyIfPageIsReady();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [viewer.id]);

  useEffect(() => {
    if (!isReady || !viewer.id || !accountId) return;

    const trackingService = new TrackingService({
      ...viewer,
      sessionId: sessionIdRef.current,
      documentId,
      accountId,
      email: viewer.email as string,
    });
    const { teardown: serviceTeardown, update, flush } = trackingService;

    const viewZone = document.querySelector(VIEW_ZONE_SELECTOR) as Element;
    const sections: Element[] = [];
    Array.from(document.querySelectorAll(SECTION_CONTAINER_SELECTOR)).forEach((container) => {
      const title = container.querySelector(SECTION_TITLE_SELECTOR);
      const section = container.querySelector(SECTION_SELECTOR);
      if (!section) return;
      if (title && title.textContent) {
        section.id = title.textContent;
      }
      if (!section.id || !section.id.trim()) return;
      sections.push(section);
    });

    const { teardown: observerTeardown } = observeVisibility({
      root: viewZone,
      targets: sections,
      dispatch: (sectionId, time) => update(sectionId, time),
    });

    window.addEventListener('pagehide', flush);

    return () => {
      window.removeEventListener('pagehide', flush);
      observerTeardown();
      serviceTeardown();
    };
  }, [isReady, documentId, viewer.id, accountId]);
};
